import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ImobiliariaOnboardingDialog } from '@/components/contratacao/ImobiliariaOnboardingDialog';
import { ContratacaoCheckoutReview } from '@/components/contratacao/ContratacaoCheckoutReview';
import { DashboardTargetDialog } from '@/components/contratacao/DashboardTargetDialog';
import { getCheckoutOrderBumps } from '@/config/checkout-order-bumps';
import { useToast } from '@/hooks/use-toast';
import { contratacaoService } from '@/services/contratacaoService';
import type {
  ContratacaoDashboardOption,
  ContratacaoPreviewResponse,
  ContratacaoRequest,
  PlanoPublico,
} from '@/api/contratacao.actions.schema';
import {
  Loader2,
  ArrowRight,
  ArrowLeft,
  Lock,
  Mail,
  Check,
  Star,
  Gift,
  AlertCircle,
  RefreshCcw,
  Shield,
  CreditCard,
  LayoutGrid,
} from 'lucide-react';
import imobdeskLogo from '@/assets/imobdesk-logo.svg';
import { useAuth } from '@/contexts/AuthContext';
import { API_BASE_URL } from '@/config/api';
import { getAttribution, captureAttribution } from '@/lib/attribution';
import { CHECKOUT_INLINE } from '@/lib/checkout-theme';
import {
  trackContratarViewed,
  trackSignupStarted,
  trackCheckoutEmailEntered,
  trackCheckoutOtpRequested,
  trackCheckoutOtpValidated,
  trackCheckoutOtpFailed,
  trackCheckoutPlanChanged,
  trackCheckoutBillingCycleChanged,
  trackCheckoutCouponApplied,
  trackCheckoutCouponInvalid,
  trackCheckoutOrderBumpToggled,
  trackCheckoutReviewViewed,
  trackPaymentInitiated,
} from '@/lib/tracking';
import {
  contratacaoCelularFieldSchema,
  contratacaoEmailFieldSchema,
  contratacaoNomeFieldSchema,
  contratacaoNomeImobiliariaFieldSchema,
} from '@/api/contratacao.schema';
import { validateOtpFormSchema } from '@/api/auth.schema';
import {
  CHECKOUT_INLINE,
  CHECKOUT_MARKETING_CLASSES,
  CHECKOUT_ROOT_STYLE,
} from '@/lib/checkout-theme';
import { cn } from '@/lib/utils';

function checkoutCtaClass(indigo = false) {
  return cn(
    'cta',
    indigo ? CHECKOUT_MARKETING_CLASSES.accentBtnIndigo : CHECKOUT_MARKETING_CLASSES.accentBtnGreen,
  );
}

type GuestAuthStep = 'email' | 'otp' | 'details';
type AuthenticatedStep = 'whatsapp' | 'details';

interface UrlQueryParams {
  ciclo?: 'mensal' | 'anual';
  trial?: number;
  card?: boolean;
  cupom?: string;
  dashboardId?: number;
  checkoutTargetToken?: string;
  nomeImobiliaria?: string;
  successUrl?: string;
  cancelUrl?: string;
}

interface FormData {
  nomeImobiliaria: string;
  siteAtual: string;
}

type DashboardCheckoutCandidate = ContratacaoDashboardOption & {
  canManageDashboardConfig: boolean;
  hasActivePlan: boolean;
  blockedReason?: string;
};

const STORAGE_KEY = 'contratar_form_data';
const PLANOS_PERMITIDOS = new Set(['basic', 'starter', 'pro', 'premier']);
const RESEND_COOLDOWN = 45;

const STEP_LABELS = ['E-mail', 'Validação', 'Detalhes'] as const;

function GoogleGIcon() {
  return (
    <svg
      aria-hidden
      width="18"
      height="18"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M21.805 10.041H12v3.918h5.629c-.243 1.242-.972 2.294-2.066 3.001v2.491h3.345c1.958-1.803 3.092-4.46 3.092-7.451 0-.67-.06-1.314-.195-1.959Z"
        fill="#4285F4"
      />
      <path
        d="M12 22c2.792 0 5.134-.926 6.845-2.549l-3.345-2.491c-.93.63-2.12 1.002-3.5 1.002-2.693 0-4.973-1.818-5.79-4.266H2.76v2.571A10 10 0 0 0 12 22Z"
        fill="#34A853"
      />
      <path
        d="M6.21 13.696A6.005 6.005 0 0 1 5.886 12c0-.59.104-1.161.324-1.696V7.733H2.76A10 10 0 0 0 2 12c0 1.613.386 3.141 1.067 4.267l3.143-2.571Z"
        fill="#FBBC05"
      />
      <path
        d="M12 6.038c1.518 0 2.877.523 3.947 1.55l2.96-2.96C17.129 2.984 14.788 2 12 2a10 10 0 0 0-8.24 5.733L6.21 10.304C7.027 7.856 9.307 6.038 12 6.038Z"
        fill="#EA4335"
      />
    </svg>
  );
}

function StepRail({ step, reducedMotion }: { step: 1 | 2 | 3; reducedMotion: boolean | null }) {
  return (
    <motion.div
      className="cx-step-rail"
      initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -8, filter: 'blur(6px)' }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      transition={{ duration: reducedMotion ? 0.14 : 0.28 }}
    >
      {STEP_LABELS.map((label, i) => {
        const idx = (i + 1) as 1 | 2 | 3;
        const state = step > idx ? 'done' : step === idx ? 'active' : 'pending';
        const numCls =
          'step-num' +
          (state === 'active' ? ' step-num--active' : '') +
          (state === 'done' ? ' step-num--done' : '');
        const labelCls = 'step-label' + (state === 'active' ? ' step-label--active' : '');
        return (
          <motion.div
            key={label}
            className="step-pill"
            initial={false}
            animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: reducedMotion ? 0.1 : 0.2, delay: reducedMotion ? 0 : i * 0.03 }}
          >
            <div className={numCls}>
              {state === 'done' ? <Check className="h-3 w-3" strokeWidth={3} /> : idx}
            </div>
            <span className={labelCls}>{label}</span>
            {i < STEP_LABELS.length - 1 ? (
              <span className="step-line">
                <span
                  className="step-line-fill"
                  style={{ width: step > idx ? '100%' : step === idx ? '50%' : '0%' }}
                />
              </span>
            ) : null}
          </motion.div>
        );
      })}
    </motion.div>
  );
}

function ProofStrip() {
  return (
    <div className="proof-strip">
      <span className="stars">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star key={i} className="h-3 w-3" fill="currentColor" />
        ))}
        <span className="rating">4.9</span>
      </span>
      <span className="sep" />
      <span>no Google</span>
      <span className="sep" />
      <span>
        <strong>+200</strong>imobiliárias
      </span>
    </div>
  );
}

function formatWhatsappMask(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (!digits) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

export default function Contratar() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated, isLoading: isAuthLoading, user, logout } = useAuth();
  const reducedMotion = useReducedMotion();

  const [plano, setPlano] = useState<PlanoPublico | null>(null);
  const [planosDisponiveis, setPlanosDisponiveis] = useState<PlanoPublico[]>([]);
  const [isLoadingPlanosDisponiveis, setIsLoadingPlanosDisponiveis] = useState(false);
  const [isLoadingPlano, setIsLoadingPlano] = useState(true);
  const [planoError, setPlanoError] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [urlParams, setUrlParams] = useState<UrlQueryParams>({});
  const [selectedCiclo, setSelectedCiclo] = useState<'mensal' | 'anual'>('mensal');
  const [cupomLocal, setCupomLocal] = useState('');
  const [selectedBumpPriceIds, setSelectedBumpPriceIds] = useState<string[]>([]);
  const [checkoutPreview, setCheckoutPreview] = useState<ContratacaoPreviewResponse['data'] | null>(null);
  const [checkoutPreviewLoading, setCheckoutPreviewLoading] = useState(false);
  const [checkoutPreviewError, setCheckoutPreviewError] = useState<string | null>(null);
  const orderBumps = useMemo(() => getCheckoutOrderBumps(), []);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [ownedDashboards, setOwnedDashboards] = useState<DashboardCheckoutCandidate[]>([]);
  const [isLoadingOwnedDashboards, setIsLoadingOwnedDashboards] = useState(false);
  const [selectedDashboardId, setSelectedDashboardId] = useState<number | null>(null);
  const [hasConfirmedDashboardChoice, setHasConfirmedDashboardChoice] = useState(false);
  const [dashboardDialogOpen, setDashboardDialogOpen] = useState(false);
  const [nomeImobiliariaTouched, setNomeImobiliariaTouched] = useState(false);
  const [authenticatedWhatsapp, setAuthenticatedWhatsapp] = useState('');
  const [authenticatedWhatsappTouched, setAuthenticatedWhatsappTouched] = useState(false);
  const [authenticatedStep, setAuthenticatedStep] = useState<AuthenticatedStep>('whatsapp');

  // guest flow state
  const [guestAuthStep, setGuestAuthStep] = useState<GuestAuthStep>('email');
  const [guestFlowLoading, setGuestFlowLoading] = useState(false);
  const [guestNeedsRegistrationData, setGuestNeedsRegistrationData] = useState(false);
  const [guestPendingCheckoutUrl, setGuestPendingCheckoutUrl] = useState<string | null>(null);
  const [guestEmail, setGuestEmail] = useState('');
  const [guestNome, setGuestNome] = useState('');
  const [guestCelular, setGuestCelular] = useState('');
  const [guestOtpDigits, setGuestOtpDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [registerTouched, setRegisterTouched] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpInputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const dashboardFromLinkNoticeRef = useRef<number | null>(null);
  const checkoutTargetNoticeRef = useRef<string | null>(null);
  const otpCode = guestOtpDigits.join('');
  const dashboardTitleById = useMemo(
    () => new Map(ownedDashboards.map((item) => [item.id, item.titulo])),
    [ownedDashboards],
  );
  const selectableDashboards = useMemo(
    () => ownedDashboards.filter((item) => item.canManageDashboardConfig && !item.hasActivePlan),
    [ownedDashboards],
  );
  const blockedDashboards = useMemo(
    () =>
      ownedDashboards
        .filter((item) => !item.canManageDashboardConfig || item.hasActivePlan)
        .map((item) => ({
          ...item,
          blockedReason: item.blockedReason || 'Não é possível contratar para esta dashboard.',
        })),
    [ownedDashboards],
  );

  const loadFromStorage = (): FormData => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as Partial<FormData>;
        return {
          nomeImobiliaria: parsed.nomeImobiliaria ?? '',
          siteAtual: parsed.siteAtual ?? '',
        };
      }
    } catch (e) {
      console.error('Erro ao carregar dados do localStorage:', e);
    }
    return { nomeImobiliaria: '', siteAtual: '' };
  };

  const [formData, setFormData] = useState<FormData>(loadFromStorage);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    } catch (e) {
      console.error('Erro ao salvar dados no localStorage:', e);
    }
  }, [formData]);

  useEffect(() => {
    if (isAuthenticated) {
      setGuestAuthStep('email');
      setGuestOtpDigits(['', '', '', '', '', '']);
      setGuestNeedsRegistrationData(false);
      setGuestPendingCheckoutUrl(null);
      setDashboardDialogOpen(false);
      setHasConfirmedDashboardChoice(false);
      setAuthenticatedStep('whatsapp');
      setAuthenticatedWhatsapp(formatWhatsappMask(user?.celular ?? ''));
      setAuthenticatedWhatsappTouched(false);
      setNomeImobiliariaTouched(false);
    }
  }, [isAuthenticated, user?.celular]);

  useEffect(() => {
    if (!isAuthenticated) {
      setOwnedDashboards([]);
      setSelectedDashboardId(null);
      setHasConfirmedDashboardChoice(false);
      setDashboardDialogOpen(false);
      return;
    }

    let active = true;

    async function fetchOwnedDashboards() {
      try {
        setIsLoadingOwnedDashboards(true);
        const data = await contratacaoService.listarDashboardsUsuario();
        if (!active) return;
        const unique = Array.from(
          new Map((Array.isArray(data) ? data : []).map((item) => [item.id, item])).values(),
        );
        const enriched = await Promise.all(
          unique.map(async (item): Promise<DashboardCheckoutCandidate> => {
            const [canManageDashboardConfig, hasActivePlan] = await Promise.all([
              contratacaoService
                .usuarioTemCapabilityNaDashboard(item.id, 'manageDashboardConfig')
                .catch(() => false),
              contratacaoService
                .buscarPlanoAtualDashboard(item.id)
                .then((res) => Boolean(res?.plano && res.plano !== false))
                .catch(() => false),
            ]);
            const blockedReason = hasActivePlan
              ? 'Não é possível contratar para esta dashboard pois ela já tem um plano ativo.'
              : !canManageDashboardConfig
                ? 'Não é possível contratar para esta dashboard pois você não é o proprietário.'
                : undefined;
            return {
              ...item,
              canManageDashboardConfig,
              hasActivePlan,
              blockedReason,
            };
          }),
        );
        if (!active) return;
        setOwnedDashboards(enriched);
        const selectable = enriched.filter(
          (item) => item.canManageDashboardConfig && !item.hasActivePlan,
        );
        let requestedDashboardId = urlParams.dashboardId;
        let shouldAutoConfirmDashboard = false;
        if (urlParams.checkoutTargetToken?.trim()) {
          const targetToken = urlParams.checkoutTargetToken.trim();
          const resolved = await contratacaoService
            .resolverCheckoutTargetToken(targetToken)
            .catch(() => ({ valid: false, reason: 'Token de checkout inválido ou expirado.' }));
          if (!active) return;
          if (resolved.valid && typeof resolved.dashboardId === 'number') {
            requestedDashboardId = resolved.dashboardId;
            shouldAutoConfirmDashboard = true;
          } else if (checkoutTargetNoticeRef.current !== targetToken) {
            checkoutTargetNoticeRef.current = targetToken;
            toast({
              title: 'Link de checkout inválido',
              description:
                resolved.reason ||
                'Não foi possível validar o destino de checkout informado no link.',
            });
          }
        }
        if (requestedDashboardId != null) {
          const requestedDashboard = enriched.find((item) => item.id === requestedDashboardId);
          if (
            requestedDashboard &&
            requestedDashboard.canManageDashboardConfig &&
            !requestedDashboard.hasActivePlan
          ) {
            setSelectedDashboardId(requestedDashboardId);
            setHasConfirmedDashboardChoice(shouldAutoConfirmDashboard);
            if (shouldAutoConfirmDashboard) {
              setDashboardDialogOpen(false);
            }
            return;
          }

          if (dashboardFromLinkNoticeRef.current !== requestedDashboardId) {
            dashboardFromLinkNoticeRef.current = requestedDashboardId;
            toast({
              title: 'Dashboard do link indisponível',
              description:
                requestedDashboard?.blockedReason ||
                'Não foi possível usar a dashboard informada no link. Escolha outra opção para continuar.',
            });
          }
        }

        if (selectable.length === 0) {
          setSelectedDashboardId(null);
          setHasConfirmedDashboardChoice(true);
        } else {
          setSelectedDashboardId((prev) => prev ?? selectable[0].id);
          setHasConfirmedDashboardChoice(false);
        }
      } catch (err) {
        if (!active) return;
        setOwnedDashboards([]);
        setSelectedDashboardId(null);
        setHasConfirmedDashboardChoice(true);
      } finally {
        if (active) setIsLoadingOwnedDashboards(false);
      }
    }

    void fetchOwnedDashboards();

    return () => {
      active = false;
    };
  }, [
    isAuthenticated,
    toast,
    urlParams.dashboardId,
    urlParams.checkoutTargetToken,
  ]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  const isTrial = urlParams.trial !== undefined && urlParams.trial > 0;
  const skipCicloChoice = !!urlParams.ciclo || isTrial;

  useEffect(() => {
    const URL_PARAMS_STORAGE_KEY = 'contratar_url_params';
    const params: UrlQueryParams = {};
    const savedParams = localStorage.getItem(URL_PARAMS_STORAGE_KEY);
    let fromStorage = false;
    if (savedParams) {
      try {
        const parsed = JSON.parse(savedParams);
        const isValid = parsed._timestamp && Date.now() - parsed._timestamp < 24 * 60 * 60 * 1000;
        if (isValid) {
          fromStorage = true;
          if (parsed.ciclo === 'mensal' || parsed.ciclo === 'anual') {
            params.ciclo = parsed.ciclo;
            setSelectedCiclo(parsed.ciclo);
          }
          if (typeof parsed.trial === 'number' && parsed.trial > 0) {
            params.trial = parsed.trial;
            setSelectedCiclo('mensal');
          }
          if (typeof parsed.card === 'boolean') params.card = parsed.card;
          if (parsed.cupom) params.cupom = parsed.cupom;
          if (typeof parsed.dashboardId === 'number' && parsed.dashboardId > 0) {
            params.dashboardId = parsed.dashboardId;
          } else if (typeof parsed.dashboardId === 'string' && /^\d+$/.test(parsed.dashboardId)) {
            const parsedDashboardId = parseInt(parsed.dashboardId, 10);
            if (parsedDashboardId > 0) params.dashboardId = parsedDashboardId;
          }
          if (typeof parsed.checkoutTargetToken === 'string' && parsed.checkoutTargetToken.trim()) {
            params.checkoutTargetToken = parsed.checkoutTargetToken.trim();
          } else if (typeof parsed.target === 'string' && parsed.target.trim()) {
            params.checkoutTargetToken = parsed.target.trim();
          }
          if (parsed.nomeImobiliaria) params.nomeImobiliaria = parsed.nomeImobiliaria;
          if (parsed.successUrl) params.successUrl = parsed.successUrl;
          if (parsed.cancelUrl) params.cancelUrl = parsed.cancelUrl;
          localStorage.removeItem(URL_PARAMS_STORAGE_KEY);
        } else {
          localStorage.removeItem(URL_PARAMS_STORAGE_KEY);
        }
      } catch {
        localStorage.removeItem(URL_PARAMS_STORAGE_KEY);
      }
    }

    if (!fromStorage) {
      const cicloParam = searchParams.get('ciclo');
      if (cicloParam === 'mensal' || cicloParam === 'anual') {
        params.ciclo = cicloParam;
        setSelectedCiclo(cicloParam);
      }
      const trialParam = searchParams.get('trial');
      if (trialParam !== null && /^\d+$/.test(trialParam)) {
        const dias = parseInt(trialParam, 10);
        if (dias >= 0) {
          params.trial = dias;
          if (dias > 0) setSelectedCiclo('mensal');
        }
      }
      const cardParam = searchParams.get('card');
      if (cardParam !== null) params.card = cardParam === 'true';
      const cupomParam = searchParams.get('cupom');
      if (cupomParam) params.cupom = cupomParam;
      const dashboardIdParam = searchParams.get('dashboardId') || searchParams.get('d');
      if (dashboardIdParam && /^\d+$/.test(dashboardIdParam)) {
        const parsedDashboardId = parseInt(dashboardIdParam, 10);
        if (parsedDashboardId > 0) params.dashboardId = parsedDashboardId;
      }
      const checkoutTargetTokenParam = searchParams.get('target');
      if (checkoutTargetTokenParam?.trim()) {
        params.checkoutTargetToken = checkoutTargetTokenParam.trim();
      }
      const nomeImobiliariaParam = searchParams.get('nomeImobiliaria');
      if (nomeImobiliariaParam) params.nomeImobiliaria = nomeImobiliariaParam;
      const successUrlParam = searchParams.get('successUrl');
      if (successUrlParam) params.successUrl = successUrlParam;
      const cancelUrlParam = searchParams.get('cancelUrl');
      if (cancelUrlParam) params.cancelUrl = cancelUrlParam;
    }
    setUrlParams(params);
    setFormData((prev) => ({
      ...prev,
      nomeImobiliaria: params.nomeImobiliaria || prev.nomeImobiliaria,
    }));
    if (params.cupom) setCupomLocal(params.cupom);
  }, [searchParams]);

  useEffect(() => {
    async function fetchPlano() {
      if (!slug) {
        setPlanoError('Plano não especificado');
        setIsLoadingPlano(false);
        return;
      }
      try {
        setIsLoadingPlano(true);
        setPlanoError(null);
        const data = await contratacaoService.buscarPlanoPorSlug(slug);
        setPlano(data);
      } catch (err) {
        setPlanoError(err instanceof Error ? err.message : 'Erro ao carregar plano');
      } finally {
        setIsLoadingPlano(false);
      }
    }
    fetchPlano();
  }, [slug]);

  // Captura inicial de atribuicao (UTMs, _ga cookie, gclid, etc.)
  useEffect(() => {
    captureAttribution();
  }, []);

  // Dispara eventos de mudanca de ciclo de cobranca
  const lastBillingCycleRef = useRef<'mensal' | 'anual' | null>(null);
  useEffect(() => {
    if (!plano) return;
    if (lastBillingCycleRef.current === null) {
      lastBillingCycleRef.current = selectedCiclo;
      return;
    }
    if (lastBillingCycleRef.current !== selectedCiclo) {
      lastBillingCycleRef.current = selectedCiclo;
      trackCheckoutBillingCycleChanged({
        planSlug: plano.slug,
        billingCycle: selectedCiclo,
      });
    }
  }, [selectedCiclo, plano]);

  // Dispara eventos de cupom (valid -> applied, invalid -> invalid) com base no preview
  const lastCouponSignatureRef = useRef<string>('');
  useEffect(() => {
    if (!plano) return;
    const code = cupomLocal.trim();
    if (!code || !checkoutPreview?.coupon) return;
    const signature = `${code}|${checkoutPreview.coupon.valid}|${checkoutPreview.discount}`;
    if (lastCouponSignatureRef.current === signature) return;
    lastCouponSignatureRef.current = signature;
    if (checkoutPreview.coupon.valid) {
      trackCheckoutCouponApplied({
        planSlug: plano.slug,
        couponCode: code,
        discountValue: checkoutPreview.discount,
        currency: checkoutPreview.currency,
      });
    } else {
      trackCheckoutCouponInvalid({
        planSlug: plano.slug,
        couponCode: code,
        reason: checkoutPreview.coupon.reason,
      });
    }
  }, [cupomLocal, checkoutPreview, plano]);

  // Dispara contratar_viewed + signup_started uma vez quando o plano carrega
  const trackedViewedRef = useRef(false);
  useEffect(() => {
    if (trackedViewedRef.current) return;
    if (!plano || isAuthLoading) return;
    trackedViewedRef.current = true;
    const billing = selectedCiclo;
    const trialDays = urlParams.trial ?? 0;
    const isTrialNow = trialDays > 0;
    const hasCoupon = Boolean(cupomLocal.trim() || urlParams.cupom?.trim());
    trackContratarViewed({
      planSlug: plano.slug,
      billingCycle: billing,
      isTrial: isTrialNow,
      trialDays,
      hasCoupon,
      isAuthenticated,
      userId: user?.id,
    });
    trackSignupStarted({
      planSlug: plano.slug,
      isTrial: isTrialNow,
      isAuthenticated,
      userId: user?.id,
    });
  }, [plano, isAuthLoading, isAuthenticated, user?.id, selectedCiclo, urlParams.trial, urlParams.cupom, cupomLocal]);

  useEffect(() => {
    let active = true;
    async function fetchPlanosDisponiveis() {
      try {
        setIsLoadingPlanosDisponiveis(true);
        const data = await contratacaoService.listarPlanos();
        if (!active) return;
        setPlanosDisponiveis(
          Array.isArray(data)
            ? data.filter((item) => {
                const itemSlug = (item.slug || '').trim().toLowerCase();
                return Boolean(itemSlug) && PLANOS_PERMITIDOS.has(itemSlug);
              })
            : [],
        );
      } catch (err) {
        console.error('Erro ao carregar lista de planos para troca:', err);
      } finally {
        if (active) setIsLoadingPlanosDisponiveis(false);
      }
    }
    void fetchPlanosDisponiveis();
    return () => {
      active = false;
    };
  }, []);

  const formatPrice = (price: number | string) => {
    const value = typeof price === 'string' ? parseFloat(price) : price;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const calculateDiscount = () => {
    if (!plano || !plano.precoAnual) return 0;
    const mensal = typeof plano.precoMensal === 'string' ? parseFloat(plano.precoMensal) : plano.precoMensal;
    const anual = typeof plano.precoAnual === 'string' ? parseFloat(plano.precoAnual) : plano.precoAnual;
    const valorAnualSemDesconto = mensal * 12;
    return Math.round(((valorAnualSemDesconto - anual) / valorAnualSemDesconto) * 100);
  };

  const calculateDiscountValue = () => {
    if (!plano || !plano.precoAnual) return 0;
    const mensal = typeof plano.precoMensal === 'string' ? parseFloat(plano.precoMensal) : plano.precoMensal;
    const anual = typeof plano.precoAnual === 'string' ? parseFloat(plano.precoAnual) : plano.precoAnual;
    return mensal * 12 - anual;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const buildCheckoutRequest = (overrides?: Partial<ContratacaoRequest>): ContratacaoRequest => {
    const trialDias = urlParams.trial !== undefined && urlParams.trial > 0 ? urlParams.trial : undefined;
    const cardValue = trialDias !== undefined ? urlParams.card !== false : undefined;
    const cupomFinal = cupomLocal.trim() || urlParams.cupom?.trim() || undefined;
    const bumps = selectedBumpPriceIds.length ? [...selectedBumpPriceIds] : undefined;
    return {
      ciclo: selectedCiclo,
      nomeImobiliaria: formData.nomeImobiliaria.trim(),
      trial: trialDias,
      card: cardValue,
      cupom: cupomFinal,
      orderBumpPriceIds: bumps,
      successUrl: urlParams.successUrl,
      cancelUrl: urlParams.cancelUrl,
      ...overrides,
    };
  };

  const buildGuestCheckoutRequest = (overrides?: Partial<ContratacaoRequest>): ContratacaoRequest => {
    const [primeiroNome, ...sobrenomePartes] = guestNome.trim().split(/\s+/).filter(Boolean);
    return buildCheckoutRequest({
      userId: undefined,
      email: guestEmail.trim().toLowerCase(),
      nome: primeiroNome || undefined,
      sobrenome: sobrenomePartes.length ? sobrenomePartes.join(' ') : undefined,
      celular: guestCelular.trim() || undefined,
      otpCode: otpCode.trim() || undefined,
      ...overrides,
    });
  };

  useEffect(() => {
    if (!slug || !plano) return;

    let active = true;
    const timerId = window.setTimeout(async () => {
      setCheckoutPreviewLoading(true);
      try {
        const response = await contratacaoService.previewCheckout(slug, {
          ciclo: selectedCiclo,
          cupom: cupomLocal.trim() || undefined,
          orderBumpPriceIds: selectedBumpPriceIds.length ? selectedBumpPriceIds : undefined,
        });
        if (!active) return;
        setCheckoutPreview(response.data);
        setCheckoutPreviewError(null);
      } catch (err) {
        if (!active) return;
        const message =
          err instanceof Error ? err.message : 'Não foi possível calcular o resumo do checkout.';
        setCheckoutPreviewError(message);
        setCheckoutPreview(null);
      } finally {
        if (active) setCheckoutPreviewLoading(false);
      }
    }, 350);

    return () => {
      active = false;
      window.clearTimeout(timerId);
    };
  }, [slug, plano, selectedCiclo, selectedBumpPriceIds, cupomLocal]);

  const buildPaymentInitiatedPayload = () => {
    if (!plano) return null;
    const trialDays = urlParams.trial ?? 0;
    const subtotal = checkoutPreview?.subtotal ?? 0;
    const discount = checkoutPreview?.discount ?? 0;
    const total = checkoutPreview?.total ?? 0;
    const currency = checkoutPreview?.currency ?? 'BRL';
    return {
      planSlug: plano.slug,
      billingCycle: selectedCiclo,
      subtotal,
      discount,
      total,
      currency,
      isTrial: trialDays > 0,
      trialDays,
    };
  };

  const fireAuthPaymentInitiated = (userId?: number) => {
    const base = buildPaymentInitiatedPayload();
    if (!base) return;
    const attribution = getAttribution();
    trackPaymentInitiated({
      ...base,
      userId,
      gaClientId: attribution.gaClientId,
    });
  };

  const fireGuestPaymentInitiated = () => {
    const base = buildPaymentInitiatedPayload();
    if (!base) return;
    const attribution = getAttribution();
    trackPaymentInitiated({
      ...base,
      gaClientId: attribution.gaClientId,
    });
  };

  const runCheckout = async (userId: number) => {
    if (!slug) return;
    setCheckoutLoading(true);
    try {
      const request = buildCheckoutRequest({
        userId,
        email: undefined,
        nome: undefined,
        sobrenome: undefined,
        cpf: undefined,
        celular: authenticatedWhatsapp.trim() || undefined,
      });
      const checkoutResponse = await contratacaoService.checkout(slug, request);
      if (checkoutResponse.requiresOTP) {
        throw new Error(
          checkoutResponse.message ||
            'É necessário concluir a verificação no app de login. Tente novamente.',
        );
      }
      if (checkoutResponse.success && checkoutResponse.url) {
        fireAuthPaymentInitiated(userId);
        localStorage.removeItem(STORAGE_KEY);
        window.location.href = checkoutResponse.url;
        return;
      }
      throw new Error(checkoutResponse.message || 'Erro ao gerar checkout');
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: err instanceof Error ? err.message : 'Erro ao processar pagamento',
      });
    } finally {
      setCheckoutLoading(false);
    }
  };

  const runDashboardCheckout = async (dashboardId: number) => {
    if (!plano) return;
    setCheckoutLoading(true);
    try {
      const payload = {
        planoId: plano.id,
        ciclo: selectedCiclo,
        trial: urlParams.trial !== undefined && urlParams.trial > 0 ? urlParams.trial : undefined,
        card:
          urlParams.trial !== undefined && urlParams.trial > 0
            ? urlParams.card !== false
            : undefined,
        cupom: cupomLocal.trim() || urlParams.cupom?.trim() || undefined,
      };
      const response = await contratacaoService.ativarPlanoDashboard(dashboardId, payload);
      if (response.url) {
        fireAuthPaymentInitiated(user?.id);
        localStorage.removeItem(STORAGE_KEY);
        window.location.href = response.url;
        return;
      }
      toast({
        title: 'Plano atualizado',
        description: response.message || 'Plano ativado para a dashboard selecionada.',
      });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: err instanceof Error ? err.message : 'Erro ao processar pagamento',
      });
    } finally {
      setCheckoutLoading(false);
    }
  };

  const runPublicCheckout = async (
    request: ContratacaoRequest,
    options?: { autoRedirectOnSuccess?: boolean },
  ) => {
    if (!slug) return;
    const autoRedirectOnSuccess = options?.autoRedirectOnSuccess ?? true;
    try {
      const checkoutResponse = await contratacaoService.checkoutPublic(slug, request);
      if (checkoutResponse.requiresOTP) {
        setGuestAuthStep('otp');
        setGuestPendingCheckoutUrl(null);
        if (checkoutResponse.email) setGuestEmail(checkoutResponse.email);
        setGuestOtpDigits(['', '', '', '', '', '']);
        setOtpError(false);
        setResendCooldown(RESEND_COOLDOWN);
        if (plano) {
          trackCheckoutOtpRequested({ planSlug: plano.slug });
        }
        toast({
          title: 'Código enviado',
          description:
            checkoutResponse.message ||
            'Enviamos um código de acesso para seu e-mail. Digite o código para continuar.',
        });
        return;
      }
      if (checkoutResponse.success && checkoutResponse.url) {
        if (autoRedirectOnSuccess) {
          fireGuestPaymentInitiated();
          localStorage.removeItem(STORAGE_KEY);
          window.location.href = checkoutResponse.url;
          return;
        }
        setGuestPendingCheckoutUrl(checkoutResponse.url);
        setGuestAuthStep('details');
        setOtpError(false);
        if (request.otpCode && plano) {
          trackCheckoutOtpValidated({ planSlug: plano.slug });
        }
        toast({
          title: 'Código validado',
          description: 'Conta validada com sucesso. Confira os detalhes antes de seguir ao pagamento.',
        });
        return;
      }
      throw new Error(checkoutResponse.message || 'Erro ao gerar checkout');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao processar contratação';
      if (request.otpCode) {
        setOtpError(true);
        if (plano) trackCheckoutOtpFailed({ planSlug: plano.slug });
      }
      toast({ variant: 'destructive', title: 'Erro', description: message });
    }
  };

  const handleGuestIdentifyStep = async () => {
    setEmailTouched(true);
    const emailResult = contratacaoEmailFieldSchema.safeParse(guestEmail);
    if (!emailResult.success) return;

    setGuestFlowLoading(true);
    try {
      const hasAccount = await contratacaoService.emailTemConta(emailResult.data);
      if (plano) {
        trackCheckoutEmailEntered({
          planSlug: plano.slug,
          emailHasAccount: hasAccount,
        });
      }
      if (!hasAccount) {
        setGuestNeedsRegistrationData(true);
        setRegisterTouched(false);
        return;
      }
      setGuestNeedsRegistrationData(false);
      setGuestPendingCheckoutUrl(null);
      await runPublicCheckout(
        buildGuestCheckoutRequest({
          nome: undefined,
          sobrenome: undefined,
          celular: undefined,
          otpCode: undefined,
        }),
      );
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: err instanceof Error ? err.message : 'Não foi possível verificar o e-mail',
      });
    } finally {
      setGuestFlowLoading(false);
    }
  };

  const handleGuestRegisterStep = async () => {
    setNomeImobiliariaTouched(true);
    const nomeImobiliariaResult = contratacaoNomeImobiliariaFieldSchema.safeParse(formData.nomeImobiliaria);
    if (!nomeImobiliariaResult.success) return;
    setRegisterTouched(true);
    const nomeResult = contratacaoNomeFieldSchema.safeParse(guestNome);
    const celularResult = contratacaoCelularFieldSchema.safeParse(guestCelular);
    if (!nomeResult.success || !celularResult.success) return;
    setGuestFlowLoading(true);
    try {
      await runPublicCheckout(
        buildGuestCheckoutRequest({ otpCode: undefined }),
      );
      setGuestNeedsRegistrationData(false);
    } finally {
      setGuestFlowLoading(false);
    }
  };

  const handleGuestOtpStep = async () => {
    const otpResult = validateOtpFormSchema.safeParse({
      email: guestEmail.trim(),
      code: otpCode,
    });
    if (!otpResult.success) {
      setOtpError(true);
      return;
    }
    setGuestFlowLoading(true);
    setOtpError(false);
    try {
      await runPublicCheckout(
        buildGuestCheckoutRequest({ otpCode: otpResult.data.code }),
        { autoRedirectOnSuccess: false },
      );
    } finally {
      setGuestFlowLoading(false);
    }
  };

  const handleResendGuestOtp = async () => {
    if (guestFlowLoading) return;
    if (!guestEmail.trim()) return;
    if (resendCooldown > 0) return;
    setGuestFlowLoading(true);
    try {
      await runPublicCheckout(
        buildGuestCheckoutRequest({ otpCode: undefined }),
      );
    } finally {
      setGuestFlowLoading(false);
    }
  };

  const handleGuestSubmit = async () => {
    if (guestFlowLoading || checkoutLoading) return;
    if (guestAuthStep === 'email') {
      if (guestNeedsRegistrationData) {
        await handleGuestRegisterStep();
        return;
      }
      await handleGuestIdentifyStep();
      return;
    }
    if (guestAuthStep === 'otp') {
      await handleGuestOtpStep();
      return;
    }
    if (guestPendingCheckoutUrl) {
      fireGuestPaymentInitiated();
      localStorage.removeItem(STORAGE_KEY);
      window.location.href = guestPendingCheckoutUrl;
      return;
    }
    setGuestAuthStep('otp');
  };

  const handleGoogleSignIn = () => {
    const base = API_BASE_URL.replace(/\/$/, '');
    const nextPath = `${window.location.pathname}${window.location.search}`;
    const googleAuthUrl = new URL(
      `${base}/auth/google`,
      window.location.origin,
    );
    googleAuthUrl.searchParams.set('return_app', 'admindesk');
    googleAuthUrl.searchParams.set('next', nextPath);
    window.location.href = googleAuthUrl.toString();
  };

  const handleOnboardingContinue = () => {
    setOnboardingOpen(false);
  };

  const handleSubmitContratacao = () => {
    if (cupomInvalid) {
      toast({
        variant: 'destructive',
        title: 'Cupom inválido',
        description:
          checkoutPreview?.coupon?.reason ||
          'Remova ou corrija o cupom para continuar com o pagamento.',
      });
      return;
    }
    if (isAuthenticated) {
      const shouldRequireNomeImobiliaria =
        !isLoadingOwnedDashboards && selectedDashboardId == null;
      const nomeImobiliariaOk = contratacaoNomeImobiliariaFieldSchema.safeParse(
        formData.nomeImobiliaria,
      ).success;
      const whatsappOk = contratacaoCelularFieldSchema.safeParse(authenticatedWhatsapp).success;
      if (authenticatedStep === 'whatsapp') {
        if (shouldRequireNomeImobiliaria && !nomeImobiliariaOk) {
          setNomeImobiliariaTouched(true);
          toast({
            variant: 'destructive',
            title: 'Nome da imobiliária obrigatório',
            description: 'Informe o nome da imobiliária para continuar.',
          });
          return;
        }
        if (!whatsappOk) {
          setAuthenticatedWhatsappTouched(true);
          toast({
            variant: 'destructive',
            title: 'WhatsApp obrigatório',
            description: 'Informe um WhatsApp válido para continuar a contratação.',
          });
          return;
        }
        setAuthenticatedStep('details');
        return;
      }
      if (!whatsappOk) {
        setAuthenticatedStep('whatsapp');
        setAuthenticatedWhatsappTouched(true);
        return;
      }
      if (shouldRequireNomeImobiliaria && !nomeImobiliariaOk) {
        setAuthenticatedStep('whatsapp');
        setNomeImobiliariaTouched(true);
        return;
      }
      if (selectableDashboards.length > 0 && !hasConfirmedDashboardChoice) {
        setDashboardDialogOpen(true);
        return;
      }
      if (
        selectedDashboardId != null &&
        selectableDashboards.some((dashboard) => dashboard.id === selectedDashboardId)
      ) {
        void runDashboardCheckout(selectedDashboardId);
        return;
      }
      const uid = user?.id;
      if (!uid) {
        toast({
          variant: 'destructive',
          title: 'Sessão',
          description: 'Não foi possível validar sua sessão. Tente novamente.',
        });
        return;
      }
      void runCheckout(uid);
      return;
    }
    void handleGuestSubmit();
  };

  const handleSwitchAccount = () => {
    logout();
    setGuestAuthStep('email');
    setGuestOtpDigits(['', '', '', '', '', '']);
    setGuestNeedsRegistrationData(false);
    setGuestPendingCheckoutUrl(null);
  };

  const handleSelecionarPlano = (nextSlug: string) => {
    if (!nextSlug || nextSlug === slug) return;
    trackCheckoutPlanChanged({ planSlugFrom: slug, planSlugTo: nextSlug });
    const query = window.location.search || '';
    navigate(`/contratar/${nextSlug}${query}`);
  };

  const handleOtpChange = (idx: number, raw: string) => {
    const digit = raw.replace(/\D/g, '').slice(-1);
    setGuestOtpDigits((prev) => {
      const next = [...prev];
      next[idx] = digit;
      return next;
    });
    setOtpError(false);
    if (digit && idx < 5) otpInputsRef.current[idx + 1]?.focus();
  };

  const handleOtpKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !guestOtpDigits[idx] && idx > 0) {
      otpInputsRef.current[idx - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = ['', '', '', '', '', ''];
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setGuestOtpDigits(next);
    setOtpError(false);
    otpInputsRef.current[Math.min(pasted.length, 5)]?.focus();
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!slug || isLoadingPlano || checkoutLoading || isLoadingOwnedDashboards) return;
    if (selectableDashboards.length === 0) return;
    if (hasConfirmedDashboardChoice) return;
    setDashboardDialogOpen(true);
  }, [
    isAuthenticated,
    slug,
    isLoadingPlano,
    checkoutLoading,
    isLoadingOwnedDashboards,
    selectableDashboards.length,
    hasConfirmedDashboardChoice,
  ]);

  // Dispara checkout_review_viewed quando entra na tela de revisao
  const trackedReviewRef = useRef<string>('');
  const showDetailsStepInternal =
    (isAuthenticated && authenticatedStep === 'details') ||
    (!isAuthenticated && guestAuthStep === 'details');
  useEffect(() => {
    if (!showDetailsStepInternal || !plano) return;
    const trialDays = urlParams.trial ?? 0;
    const subtotal = checkoutPreview?.subtotal ?? 0;
    const discount = checkoutPreview?.discount ?? 0;
    const total = checkoutPreview?.total ?? 0;
    const currency = checkoutPreview?.currency ?? 'BRL';
    const signature = `${plano.slug}|${selectedCiclo}|${total}|${subtotal}|${discount}`;
    if (trackedReviewRef.current === signature) return;
    trackedReviewRef.current = signature;
    trackCheckoutReviewViewed({
      planSlug: plano.slug,
      billingCycle: selectedCiclo,
      subtotal,
      discount,
      total,
      currency,
      isTrial: trialDays > 0,
      trialDays,
    });
  }, [showDetailsStepInternal, plano, selectedCiclo, checkoutPreview, urlParams.trial]);

  if (isLoadingPlano) {
    return (
      <div className="cx cx--dark" style={CHECKOUT_ROOT_STYLE}>
        <div className="cx-top">
          <img src={imobdeskLogo} alt="ImobDesk" className="cx-logo cx-logo--invert" />
        </div>
        <div className="cx-main cx-main--cardfloat">
          <div className="cx-left">
            <div className="cx-left-inner flex flex-col items-center gap-3 py-12">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
              <p className="text-sm text-slate-500">Carregando plano...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (planoError || !plano) {
    return (
      <div className="cx cx--dark" style={CHECKOUT_ROOT_STYLE}>
        <div className="cx-top">
          <img src={imobdeskLogo} alt="ImobDesk" className="cx-logo cx-logo--invert" />
        </div>
        <div className="cx-main cx-main--cardfloat">
          <div className="cx-left">
            <div className="cx-left-inner text-center">
              <p className="text-7xl font-extrabold text-slate-200">404</p>
              <h1 className="h-step mt-4">Plano não encontrado</h1>
              <p className="t-step">{planoError || 'O plano solicitado não existe ou não está disponível.'}</p>
              <button className={checkoutCtaClass(true)} onClick={() => navigate('/')}>
                <ArrowLeft className="h-5 w-5" /> Voltar para o início
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (checkoutLoading) {
    return (
      <div className="cx cx--dark" style={CHECKOUT_ROOT_STYLE}>
        <div className="cx-top">
          <img src={imobdeskLogo} alt="ImobDesk" className="cx-logo cx-logo--invert" />
        </div>
        <div className="cx-main cx-main--cardfloat">
          <div className="cx-left">
            <div className="cx-left-inner flex flex-col items-center gap-3 py-16">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
              <p className="text-sm text-slate-500">Redirecionando para o pagamento...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const desconto = calculateDiscount();
  const descontoValor = calculateDiscountValue();
  const trialDays = urlParams.trial ?? 0;
  const precoMensalAnual = plano.precoAnual
    ? (typeof plano.precoAnual === 'string' ? parseFloat(plano.precoAnual) : plano.precoAnual) / 12
    : null;

  const showDetailsStep =
    (isAuthenticated && authenticatedStep === 'details') ||
    (!isAuthenticated && guestAuthStep === 'details');
  const currentStep: 1 | 2 | 3 =
    showDetailsStep ? 3 : guestAuthStep === 'otp' ? 2 : 1;

  const validEmail = contratacaoEmailFieldSchema.safeParse(guestEmail).success;
  const isActionLoading = checkoutLoading || guestFlowLoading || isAuthLoading;
  const cupomInvalid =
    Boolean(cupomLocal.trim()) && Boolean(checkoutPreview?.coupon && !checkoutPreview.coupon.valid);
  const canProceedToPayment =
    (isAuthenticated || guestAuthStep !== 'details' || !!guestPendingCheckoutUrl) && !cupomInvalid;

  const headerTitleStep1 = isTrial ? 'Inicie seu teste grátis' : 'Contrate o plano agora';
  const headerSubtitleStep1 = isTrial
    ? 'Use seu e-mail para começar. Em segundos sua conta de teste estará pronta.'
    : 'Use seu e-mail para começar. Em segundos sua conta estará pronta.';

  const ctaLabel = (() => {
    if (isAuthenticated) {
      if (authenticatedStep === 'whatsapp') return 'Continuar';
      return isTrial ? 'Iniciar teste grátis' : 'Ir para o pagamento';
    }
    if (guestAuthStep === 'email')
      return 'Continuar';
    if (guestAuthStep === 'otp') return 'Validar código';
    return isTrial ? 'Iniciar teste grátis' : 'Ir para o pagamento';
  })();

  const detailsCtaIndigo = !isTrial;
  const showProofStrip = !showDetailsStep;
  const stepMotionProps = reducedMotion
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { duration: 0.16 },
      }
    : {
        initial: { opacity: 0, y: 16, filter: 'blur(10px)' },
        animate: { opacity: 1, y: 0, filter: 'blur(0px)' },
        exit: { opacity: 0, y: -10, filter: 'blur(6px)' },
        transition: { duration: 0.32, ease: [0.16, 1, 0.3, 1] as const },
      };
  const contentStagger = reducedMotion
    ? {
        hidden: { opacity: 1 },
        visible: { opacity: 1, transition: { staggerChildren: 0 } },
      }
    : {
        hidden: { opacity: 1 },
        visible: { opacity: 1, transition: { staggerChildren: 0.045, delayChildren: 0.02 } },
      };
  const contentItem = reducedMotion
    ? {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.14 } },
      }
    : {
        hidden: { opacity: 0, y: 8, filter: 'blur(5px)' },
        visible: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.24 } },
      };
  const ctaMotion = reducedMotion
    ? {}
    : {
        whileHover: { y: -1, scale: 1.01 },
        whileTap: { y: 0, scale: 0.99 },
        transition: { type: 'spring', stiffness: 280, damping: 20 },
      };
  const securitySealPulse = reducedMotion
    ? {}
    : {
        animate: {
          scale: [1, 1.02, 1],
          opacity: [0.92, 1, 0.92],
        },
        transition: {
          duration: 2.6,
          repeat: Infinity,
          ease: 'easeInOut' as const,
        },
      };

  return (
    <>
      <div className="cx cx--dark" style={CHECKOUT_ROOT_STYLE}>
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          {!reducedMotion ? (
            <>
              <motion.div
                className="absolute -top-24 right-[8%] h-80 w-80 rounded-full bg-indigo-500/25 blur-[92px]"
                initial={{ opacity: 0.32 }}
                animate={{ opacity: [0.28, 0.44, 0.28], scale: [1, 1.06, 1] }}
                transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut' }}
              />
              <motion.div
                className="absolute -bottom-32 -left-28 h-96 w-96 rounded-full bg-violet-500/20 blur-[100px]"
                initial={{ opacity: 0.24 }}
                animate={{ opacity: [0.2, 0.36, 0.2], scale: [1, 1.05, 1] }}
                transition={{ duration: 12.5, repeat: Infinity, ease: 'easeInOut', delay: 0.6 }}
              />
            </>
          ) : (
            <>
              <div className="absolute -top-24 right-[8%] h-80 w-80 rounded-full bg-indigo-500/18 blur-[92px]" />
              <div className="absolute -bottom-32 -left-28 h-96 w-96 rounded-full bg-violet-500/14 blur-[100px]" />
            </>
          )}
        </div>

        <div className="cx-top">
          <img src={imobdeskLogo} alt="ImobDesk" className="cx-logo cx-logo--invert" />
        </div>

        {!isAuthenticated ? <StepRail step={currentStep} reducedMotion={reducedMotion} /> : null}

        <div className="cx-main cx-main--cardfloat">
          <div className="cx-left">
            <motion.div
              className="cx-left-inner"
              initial={
                reducedMotion
                  ? { opacity: 0 }
                  : { opacity: 0, y: 18, filter: 'blur(18px)', scale: 0.985 }
              }
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)', scale: 1 }}
              transition={{ duration: reducedMotion ? 0.16 : 0.45, ease: [0.16, 1, 0.3, 1] }}
            >
              {showProofStrip ? (
                <motion.div
                  initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: reducedMotion ? 0.16 : 0.3 }}
                >
                  <ProofStrip />
                </motion.div>
              ) : null}

              <AnimatePresence mode="wait" initial={false}>
                {/* Step 1 — e-mail (+ optional account creation) */}
                {!isAuthenticated && guestAuthStep === 'email' ? (
                  <motion.div
                    className="fade-enter"
                    key="step-email"
                    {...stepMotionProps}
                  >
                  <motion.div variants={contentStagger} initial="hidden" animate="visible">
                  {isTrial ? (
                    <motion.span className="tag-trial" variants={contentItem}>
                      <Gift /> {trialDays} dia{trialDays !== 1 ? 's' : ''} grátis · sem compromisso
                    </motion.span>
                  ) : null}
                  <motion.h1 className="h-step" variants={contentItem}>{headerTitleStep1}</motion.h1>
                  <motion.p className="t-step" variants={contentItem}>{headerSubtitleStep1}</motion.p>

                  <motion.div className="field" variants={contentItem}>
                    <label className="label" htmlFor="contratar-email">
                      Informe seu e-mail
                    </label>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        id="contratar-email"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        placeholder="voce@imobiliaria.com.br"
                        className={`input-xl input-xl--with-leading-icon ${emailTouched && !validEmail ? 'is-error' : ''}`}
                        value={guestEmail}
                        onChange={(e) => {
                          setGuestEmail(e.target.value.toLowerCase());
                          setGuestNeedsRegistrationData(false);
                          setGuestNome('');
                          setGuestCelular('');
                        }}
                        disabled={guestFlowLoading}
                      />
                    </div>
                    {emailTouched && !validEmail ? (
                      <div className="field-error">
                        <AlertCircle /> Digite um e-mail válido
                      </div>
                    ) : null}
                  </motion.div>

                  {guestNeedsRegistrationData ? (
                    <motion.div className="fade-enter" variants={contentItem}>
                      <p className="field-hint" style={{ margin: '4px 0 14px' }}>
                        <strong className="text-slate-900">Vamos criar sua conta.</strong>{' '}
                        Não encontramos esse e-mail no ImobDesk.
                      </p>
                      <div className="field">
                        <label className="label" htmlFor="contratar-nome">Seu nome</label>
                        <input
                          id="contratar-nome"
                          className={`input-xl ${registerTouched && !contratacaoNomeFieldSchema.safeParse(guestNome).success ? 'is-error' : ''}`}
                          placeholder="Como devemos te chamar?"
                          autoComplete="name"
                          value={guestNome}
                          onChange={(e) => setGuestNome(e.target.value)}
                          disabled={guestFlowLoading}
                        />
                      </div>
                      <div className="field">
                        <label className="label" htmlFor="contratar-imobiliaria-nome">
                          Nome da imobiliária
                        </label>
                        <input
                          id="contratar-imobiliaria-nome"
                          className={`input-xl ${
                            nomeImobiliariaTouched &&
                            !contratacaoNomeImobiliariaFieldSchema.safeParse(formData.nomeImobiliaria)
                              .success
                              ? 'is-error'
                              : ''
                          }`}
                          placeholder="Ex.: Imobiliária Horizonte"
                          autoComplete="organization"
                          value={formData.nomeImobiliaria}
                          onChange={(e) =>
                            handleInputChange('nomeImobiliaria', e.target.value)
                          }
                          onBlur={() => setNomeImobiliariaTouched(true)}
                          disabled={guestFlowLoading}
                        />
                        {nomeImobiliariaTouched &&
                        !contratacaoNomeImobiliariaFieldSchema.safeParse(formData.nomeImobiliaria).success ? (
                          <div className="field-error">
                            <AlertCircle /> Digite o nome da imobiliária.
                          </div>
                        ) : null}
                      </div>
                      <div className="field">
                        <label className="label" htmlFor="contratar-whatsapp">WhatsApp</label>
                        <input
                          id="contratar-whatsapp"
                          inputMode="tel"
                          className={`input-xl ${registerTouched && !contratacaoCelularFieldSchema.safeParse(guestCelular).success ? 'is-error' : ''}`}
                          placeholder="(47) 99000-0000"
                          autoComplete="tel"
                          value={guestCelular}
                          onChange={(e) => setGuestCelular(e.target.value)}
                          disabled={guestFlowLoading}
                        />
                      </div>
                    </motion.div>
                  ) : null}

                  <motion.button
                    className={checkoutCtaClass()}
                    onClick={handleSubmitContratacao}
                    disabled={isActionLoading}
                    variants={contentItem}
                    {...ctaMotion}
                  >
                    {isActionLoading ? 'Processando...' : ctaLabel}
                    {!isActionLoading ? <ArrowRight className="h-5 w-5" /> : null}
                  </motion.button>

                  <motion.div
                    variants={contentItem}
                    className="flex items-center gap-3"
                    style={{ marginTop: 18, marginBottom: 14 }}
                  >
                    <span className="h-px flex-1 bg-slate-200" />
                    <span className="text-xs font-medium uppercase tracking-[0.12em] text-slate-500">
                      ou
                    </span>
                    <span className="h-px flex-1 bg-slate-200" />
                  </motion.div>

                  <motion.button
                    type="button"
                    className="cta"
                    style={CHECKOUT_INLINE.googleButton}
                    onClick={handleGoogleSignIn}
                    disabled={isActionLoading}
                    variants={contentItem}
                    {...ctaMotion}
                  >
                    <GoogleGIcon />
                    Continuar com Google
                  </motion.button>

                  <motion.p className="terms" variants={contentItem}>
                    Ao continuar, você concorda com os{' '}
                    <a href="https://imobdesk.com.br/termos-de-uso" target="_blank" rel="noopener noreferrer">
                      Termos de Uso
                    </a>{' '}
                    e a{' '}
                    <a href="https://imobdesk.com.br/privacidade" target="_blank" rel="noopener noreferrer">
                      Política de Privacidade
                    </a>{' '}
                    da ImobDesk.
                  </motion.p>
                  </motion.div>
                  </motion.div>
                ) : null}

                {/* Step 2 (auth) — WhatsApp */}
                {isAuthenticated && authenticatedStep === 'whatsapp' ? (
                  <motion.div
                    className="fade-enter"
                    key="step-auth-whatsapp"
                    {...stepMotionProps}
                  >
                    <motion.div variants={contentStagger} initial="hidden" animate="visible">
                      <motion.span className="eyebrow" variants={contentItem}>
                        <span className="pip" /> Etapa de validação
                      </motion.span>
                      <motion.h1 className="h-step" variants={contentItem}>
                        Confirme seu WhatsApp de contato
                      </motion.h1>
                      <motion.p className="t-step" variants={contentItem}>
                        Precisamos desse número para avisos importantes da contratação e da sua assinatura.
                      </motion.p>

                      <motion.div className="email-pill" variants={contentItem}>
                        <Mail />
                        <span className="em-text">{user?.email}</span>
                        <button onClick={handleSwitchAccount}>Trocar conta</button>
                      </motion.div>

                      <motion.div className="field" variants={contentItem}>
                        <label className="label" htmlFor="contratar-auth-whatsapp">
                          WhatsApp para contato
                        </label>
                        <input
                          id="contratar-auth-whatsapp"
                          inputMode="tel"
                          autoComplete="tel"
                          className={`input-xl ${
                            authenticatedWhatsappTouched &&
                            !contratacaoCelularFieldSchema.safeParse(authenticatedWhatsapp).success
                              ? 'is-error'
                              : ''
                          }`}
                          placeholder="(47) 99000-0000"
                          value={authenticatedWhatsapp}
                          onChange={(e) =>
                            setAuthenticatedWhatsapp(formatWhatsappMask(e.target.value))
                          }
                          onBlur={() => setAuthenticatedWhatsappTouched(true)}
                          disabled={isActionLoading}
                        />
                        {authenticatedWhatsappTouched &&
                        !contratacaoCelularFieldSchema.safeParse(authenticatedWhatsapp).success ? (
                          <div className="field-error">
                            <AlertCircle /> Digite um WhatsApp válido.
                          </div>
                        ) : null}
                      </motion.div>

                      {selectedDashboardId == null ? (
                        <motion.div className="field" variants={contentItem}>
                          <label className="label" htmlFor="contratar-auth-imobiliaria-nome">
                            Nome da imobiliária
                          </label>
                          <input
                            id="contratar-auth-imobiliaria-nome"
                            className={`input-xl ${
                              nomeImobiliariaTouched &&
                              !contratacaoNomeImobiliariaFieldSchema.safeParse(formData.nomeImobiliaria)
                                .success
                                ? 'is-error'
                                : ''
                            }`}
                            placeholder="Ex.: Imobiliária Horizonte"
                            autoComplete="organization"
                            value={formData.nomeImobiliaria}
                            onChange={(e) =>
                              handleInputChange('nomeImobiliaria', e.target.value)
                            }
                            onBlur={() => setNomeImobiliariaTouched(true)}
                            disabled={isActionLoading}
                          />
                          {nomeImobiliariaTouched &&
                          !contratacaoNomeImobiliariaFieldSchema.safeParse(formData.nomeImobiliaria).success ? (
                            <div className="field-error">
                              <AlertCircle /> Digite o nome da imobiliária.
                            </div>
                          ) : null}
                        </motion.div>
                      ) : null}

                      <motion.button
                        className={checkoutCtaClass()}
                        onClick={handleSubmitContratacao}
                        disabled={isActionLoading}
                        variants={contentItem}
                        {...ctaMotion}
                      >
                        {isActionLoading ? 'Processando...' : 'Continuar'}
                        {!isActionLoading ? <ArrowRight className="h-5 w-5" /> : null}
                      </motion.button>
                    </motion.div>
                  </motion.div>
                ) : null}

                {/* Step 2 — OTP */}
                {!isAuthenticated && guestAuthStep === 'otp' ? (
                  <motion.div
                    className="fade-enter"
                    key="step-otp"
                    {...stepMotionProps}
                  >
                  <motion.div variants={contentStagger} initial="hidden" animate="visible">
                  <motion.span className="eyebrow" variants={contentItem}>
                    <span className="pip" /> Etapa 2 de 3
                  </motion.span>
                  <motion.h1 className="h-step" variants={contentItem}>Digite o código que enviamos</motion.h1>
                  <motion.p className="t-step" variants={contentItem}>Enviamos um código de 6 dígitos para você confirmar o acesso.</motion.p>

                  <motion.div className="email-pill" variants={contentItem}>
                    <Mail />
                    <span className="em-text">{guestEmail || 'voce@imobiliaria.com.br'}</span>
                    <button
                      onClick={() => {
                        setGuestAuthStep('email');
                        setGuestOtpDigits(['', '', '', '', '', '']);
                        setOtpError(false);
                      }}
                      disabled={guestFlowLoading}
                    >
                      Alterar
                    </button>
                  </motion.div>

                  <motion.div className={`otp-row ${otpError ? 'is-shaking' : ''}`} variants={contentItem}>
                    {guestOtpDigits.map((digit, i) => (
                      <input
                        key={i}
                        ref={(el) => (otpInputsRef.current[i] = el)}
                        className={`otp-input ${digit ? 'is-filled' : ''} ${otpError ? 'is-error' : ''}`}
                        maxLength={1}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        value={digit}
                        onChange={(e) => handleOtpChange(i, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(i, e)}
                        onPaste={handleOtpPaste}
                        disabled={guestFlowLoading}
                      />
                    ))}
                  </motion.div>
                  {otpError ? (
                    <div className="field-error" style={{ marginBottom: 10 }}>
                      <AlertCircle /> Código inválido. Verifique e tente novamente.
                    </div>
                  ) : null}

                  <motion.button
                    className={checkoutCtaClass()}
                    style={{ marginTop: 18 }}
                    onClick={handleSubmitContratacao}
                    disabled={isActionLoading || otpCode.length < 6}
                    variants={contentItem}
                    {...ctaMotion}
                  >
                    {isActionLoading ? 'Validando...' : 'Validar código'}
                    {!isActionLoading ? <ArrowRight className="h-5 w-5" /> : null}
                  </motion.button>

                  <motion.div className="timer-box" variants={contentItem}>
                    <button
                      type="button"
                      className="linkbtn"
                      onClick={() => {
                        setGuestAuthStep('email');
                        setGuestOtpDigits(['', '', '', '', '', '']);
                        setOtpError(false);
                      }}
                      disabled={guestFlowLoading}
                    >
                      <ArrowLeft className="h-3 w-3" /> Alterar e-mail
                    </button>
                    {resendCooldown > 0 ? (
                      <span className="text-xs text-slate-500">
                        Reenviar em <strong>{resendCooldown}s</strong>
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="linkbtn"
                        onClick={() => void handleResendGuestOtp()}
                        disabled={guestFlowLoading}
                      >
                        <RefreshCcw className="h-3 w-3" /> Reenviar código
                      </button>
                    )}
                  </motion.div>
                  </motion.div>
                  </motion.div>
                ) : null}

                {/* Step 3 — details */}
                {showDetailsStep ? (
                  <motion.div
                    className="fade-enter"
                    key="step-details"
                    {...stepMotionProps}
                  >
                  <motion.div variants={contentStagger} initial="hidden" animate="visible">
                  {!isAuthenticated ? (
                    <motion.span className="eyebrow" variants={contentItem}>
                      <span className="pip" /> Etapa 3 de 3
                    </motion.span>
                  ) : null}
                  <motion.h1 className="h-step" variants={contentItem}>
                    {isAuthenticated ? 'Revise e confirme' : 'Revise os detalhes da sua compra'}
                  </motion.h1>
                  <motion.p className="t-step" variants={contentItem}>
                    {isTrial
                      ? `Sua imobiliária terá ${trialDays} dia${trialDays !== 1 ? 's' : ''} para usar tudo. ${
                          urlParams.card !== false
                            ? 'Cobramos só após o teste — você pode cancelar antes.'
                            : 'Sem cartão obrigatório agora.'
                        }`
                      : 'Confirme as informações abaixo e siga para o pagamento seguro via Stripe.'}
                  </motion.p>

                  {isAuthenticated ? (
                    <div className="email-pill">
                      <Mail />
                      <span className="em-text">{user?.email}</span>
                      <button onClick={handleSwitchAccount}>Trocar conta</button>
                    </div>
                  ) : (
                    <div className="email-pill" style={CHECKOUT_INLINE.emailVerifiedPill}>
                      <Check />
                      <span className="em-text">Código validado · {guestEmail}</span>
                    </div>
                  )}

                  {isAuthenticated ? (
                    <div
                      className="email-pill"
                      style={{
                        marginTop: 12,
                        ...CHECKOUT_INLINE.emailPendingPill,
                      }}
                    >
                      <LayoutGrid />
                      <span className="em-text">
                        {selectedDashboardId != null
                          ? `Plano para ${dashboardTitleById.get(selectedDashboardId) || `dashboard #${selectedDashboardId}`}`
                          : 'Plano para nova dashboard'}
                      </span>
                      {selectableDashboards.length > 0 ? (
                        <button onClick={() => setDashboardDialogOpen(true)}>
                          Alterar
                        </button>
                      ) : null}
                    </div>
                  ) : null}

                  <ContratacaoCheckoutReview
                    plano={plano}
                    selectedCiclo={selectedCiclo}
                    onSelectCiclo={setSelectedCiclo}
                    skipCicloChoice={skipCicloChoice}
                    urlCiclo={urlParams.ciclo}
                    isTrial={isTrial}
                    trialDays={urlParams.trial}
                    trialCardRequired={urlParams.card}
                    descontoPct={desconto}
                    descontoValor={descontoValor}
                    formatPrice={formatPrice}
                    precoMensalAnual={precoMensalAnual}
                    cupom={cupomLocal}
                    onCupomChange={setCupomLocal}
                    cupomFromUrl={urlParams.cupom}
                    bumps={orderBumps}
                    selectedBumpPriceIds={selectedBumpPriceIds}
                    onToggleBump={(stripePriceId, selected) => {
                      setSelectedBumpPriceIds((prev) => {
                        if (selected) return prev.includes(stripePriceId) ? prev : [...prev, stripePriceId];
                        return prev.filter((id) => id !== stripePriceId);
                      });
                      if (plano) {
                        trackCheckoutOrderBumpToggled({
                          planSlug: plano.slug,
                          bumpPriceId: stripePriceId,
                          selected,
                        });
                      }
                    }}
                    onAlterarImobiliaria={() => {
                      setOnboardingOpen(true);
                    }}
                    nomeImobiliaria={formData.nomeImobiliaria}
                    siteAtual={formData.siteAtual}
                    planosDisponiveis={planosDisponiveis}
                    currentPlanoSlug={slug}
                    isLoadingPlanosDisponiveis={isLoadingPlanosDisponiveis}
                    onSelecionarPlano={handleSelecionarPlano}
                    pricingPreview={checkoutPreview}
                    isPricingPreviewLoading={checkoutPreviewLoading}
                  />

                  {checkoutPreviewError ? (
                    <div className="card-req" style={{ marginTop: 12 }}>
                      <AlertCircle />
                      <span>{checkoutPreviewError}</span>
                    </div>
                  ) : null}

                  {isTrial ? (
                    urlParams.card !== false ? (
                      <div className="card-req">
                        <CreditCard />
                        <span>
                          <strong>Cartão obrigatório.</strong> Cadastre um cartão para iniciar o teste — não cobramos nada agora,
                          a primeira cobrança é só após os {trialDays} dia{trialDays !== 1 ? 's' : ''}.
                        </span>
                      </div>
                    ) : (
                      <div className="card-req ok">
                        <Check />
                        <span>
                          <strong>Sem cartão necessário.</strong> Você pode iniciar o teste agora e cadastrar a forma de pagamento depois.
                        </span>
                      </div>
                    )
                  ) : null}

                  <motion.button
                    className={checkoutCtaClass(detailsCtaIndigo)}
                    style={{ marginTop: 18 }}
                    onClick={handleSubmitContratacao}
                    disabled={isActionLoading || !canProceedToPayment}
                    variants={contentItem}
                    {...ctaMotion}
                  >
                    {isActionLoading ? (
                      'Processando...'
                    ) : (
                      <>
                        {isTrial ? <Gift className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                        {ctaLabel}
                        <ArrowRight className="h-5 w-5" />
                      </>
                    )}
                  </motion.button>

                  <div className="trust">
                    <motion.span
                      className="seal"
                      variants={contentItem}
                      {...securitySealPulse}
                    >
                      <Shield /> Pagamento seguro via Stripe
                    </motion.span>
                    <motion.span
                      className="seal"
                      variants={contentItem}
                      {...securitySealPulse}
                      transition={
                        reducedMotion
                          ? undefined
                          : {
                              duration: 2.6,
                              repeat: Infinity,
                              ease: 'easeInOut',
                              delay: 0.4,
                            }
                      }
                    >
                      <Lock /> SSL · LGPD
                    </motion.span>
                  </div>

                  <motion.p className="terms" variants={contentItem}>
                    Ao confirmar, você concorda com os{' '}
                    <a href="https://imobdesk.com.br/termos-de-uso" target="_blank" rel="noopener noreferrer">
                      Termos de Uso
                    </a>
                    ,{' '}
                    <a href="https://imobdesk.com.br/privacidade" target="_blank" rel="noopener noreferrer">
                      Política de Privacidade
                    </a>{' '}
                    e autoriza a renovação automática conforme o ciclo escolhido.
                  </motion.p>
                  </motion.div>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </div>

      <DashboardTargetDialog
        open={dashboardDialogOpen}
        loading={isLoadingOwnedDashboards}
        dashboards={selectableDashboards}
        blockedDashboards={blockedDashboards}
        selectedDashboardId={selectedDashboardId}
        onSelectDashboard={setSelectedDashboardId}
        onConfirm={() => {
          setHasConfirmedDashboardChoice(true);
          setDashboardDialogOpen(false);
        }}
        onOpenChange={setDashboardDialogOpen}
      />

      <ImobiliariaOnboardingDialog
        open={onboardingOpen}
        isTrial={isTrial}
        nomeImobiliaria={formData.nomeImobiliaria}
        siteAtual={formData.siteAtual}
        onNomeChange={(value) => {
          handleInputChange('nomeImobiliaria', value);
        }}
        onSiteChange={(value) => handleInputChange('siteAtual', value)}
        nomeError={undefined}
        onContinue={handleOnboardingContinue}
      />
    </>
  );
}
